<template>
  <div class="flex flex-col items-center justify-center min-h-[calc(100vh-4rem)] bg-gray-50 px-4 py-8">
    <!-- Köhler Avatar -->
    <img
      src="/src/assets/rich-radce-big.png"
      alt="Köhler Inteligent"
      class="w-24 h-24 sm:w-28 sm:h-28 rounded-full shadow-lg mb-4"
      draggable="false"
    />

    <!-- Title -->
    <h1 class="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-2">
      Köhler Inteligent
    </h1>
    <p class="text-lg text-gray-600 text-center mb-6">
      expert na tepelná čerpadla
    </p>

    <!-- Heat Pump Image -->
    <img
      src="/src/assets/hotjet-zetxe.jpg"
      alt="HOTJET tepelné čerpadlo"
      class="w-40 h-40 sm:w-48 sm:h-48 object-contain rounded-lg shadow-md mb-8"
      draggable="false"
    />

    <!-- Chatbot Button -->
    <button
      @click="$emit('enterChat')"
      class="px-8 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl shadow-lg transition transform hover:scale-105 mb-8"
    >
      💬 Chatbot
    </button>

    <!-- Quick Questions -->
    <div v-if="quickQuestions.length > 0" class="w-full max-w-2xl">
      <h2 class="text-sm font-medium text-gray-500 uppercase tracking-wide text-center mb-4">
        Rychlé otázky
      </h2>
      <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        <button
          v-for="q in quickQuestions"
          :key="q.id"
          @click="$emit('quickQuestion', q.text)"
          class="flex flex-col items-center p-4 bg-white rounded-xl border border-gray-200 hover:border-indigo-300 hover:shadow-md transition group"
        >
          <span class="text-2xl mb-2">{{ q.emoji }}</span>
          <span class="text-sm font-medium text-gray-700 group-hover:text-indigo-600 text-center">
            {{ q.name }}
          </span>
          <span v-if="q.description" class="text-xs text-gray-400 text-center mt-1 line-clamp-2">
            {{ q.description }}
          </span>
        </button>
      </div>
    </div>

    <!-- Loading state -->
    <div v-else-if="loading" class="flex items-center gap-2 text-gray-500">
      <div class="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-500"></div>
      <span>Načítám...</span>
    </div>

    <!-- Version & Changelog -->
    <div v-if="versionInfo" class="w-full max-w-2xl mt-8">
      <!-- Version Badge -->
      <div class="flex items-center justify-center gap-3 mb-4">
        <span class="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-mono rounded-full">
          v{{ versionInfo.version }}
        </span>
        <span class="px-3 py-1 bg-blue-50 text-blue-600 text-xs font-mono rounded-full">
          {{ versionInfo.gitHash }}
        </span>
        <span class="px-3 py-1 bg-green-50 text-green-600 text-xs font-mono rounded-full">
          {{ versionInfo.gitBranch }}
        </span>
        <span class="text-xs text-gray-400">
          {{ formatBuildDate(versionInfo.buildDate) }}
        </span>
      </div>

      <!-- Changelog -->
      <details v-if="versionInfo.changelog" class="group">
        <summary class="text-sm font-medium text-gray-400 uppercase tracking-wide text-center mb-2 cursor-pointer hover:text-gray-600 list-none flex items-center justify-center gap-2">
          <span>Changelog</span>
          <svg class="w-4 h-4 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </summary>
        <div class="mt-3 bg-white rounded-lg border border-gray-200 divide-y divide-gray-100 text-sm">
          <div
            v-for="(commit, index) in versionInfo.changelog.slice(0, 8)"
            :key="commit.hash"
            class="px-4 py-2 flex items-start gap-3"
            :class="index === 0 ? 'bg-green-50' : ''"
          >
            <code class="text-xs text-gray-400 font-mono mt-0.5">{{ commit.hash }}</code>
            <span class="flex-1 text-gray-700">{{ commit.message }}</span>
            <span class="text-xs text-gray-400">{{ commit.date }}</span>
          </div>
        </div>
      </details>
    </div>

    <!-- Building Management Shortcut -->
    <button
      @click="emit('openBuildingManagement')"
      class="fixed bottom-4 right-4 p-3 bg-green-500 hover:bg-green-600 text-white rounded-full shadow-lg transition z-40"
      title="Správa budov"
    >
      <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
      </svg>
    </button>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';

const emit = defineEmits(['enterChat', 'quickQuestion', 'openBuildingManagement']);

const quickQuestions = ref([]);
const loading = ref(true);
const versionInfo = ref(null);

async function loadQuickQuestions() {
  try {
    const res = await fetch('/api/quick-questions/list');
    const data = await res.json();
    if (data.success) {
      quickQuestions.value = data.questions;
    }
  } catch (error) {
    console.error('Error loading quick questions:', error);
  } finally {
    loading.value = false;
  }
}

async function loadVersionInfo() {
  try {
    const res = await fetch('/version.json');
    versionInfo.value = await res.json();
  } catch (error) {
    console.error('Error loading version info:', error);
  }
}

function formatBuildDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString('cs-CZ', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
}

onMounted(() => {
  loadQuickQuestions();
  loadVersionInfo();
});
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
